// This composable is used to get payment fields for all forms
import Plan from '@/classes/Plan'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { useSessionStore } from '@/stores/session'
import type { CreditCard, DirectBill, EFTPayment } from '@/types'
import type { ScenarioOutput } from '@precoa/calculator-sdk'
import { PaymentType, Term } from '@precoa/calculator-sdk'

let sessionStore: ReturnType<typeof useSessionStore> | null = null
let planHolder: Plan | undefined = undefined
let planHolderId: string | undefined = undefined
let paymentDetails: ScenarioOutput | undefined = undefined

export const usePaymentSetup = () => {
	const paymentSetup = (id: string, options?: string[]) => {
		planHolderId = id
		sessionStore = useSessionStore()

		setPlanHolder()

		if (options?.includes('paymentDetails')) {
			setPaymentDetails()
		}
	}

	const setPlanHolder = () => {
		if (!planHolderId || !sessionStore) {
			throw new Error('Please run setup first')
		}

		planHolder = sessionStore?.getPlanHolder(planHolderId)
	}

	const setPaymentDetails = () => {
		if (!planHolderId || !sessionStore) {
			throw new Error('Please run setup first')
		}

		const { getPlanPaymentOptions } = usePaymentOptions()
		const scenarioOutput = getPlanPaymentOptions(planHolder!).scenarioOutput
			.value

		if (scenarioOutput && !('errors' in scenarioOutput)) {
			paymentDetails = scenarioOutput
		}
	}

	return {
		paymentSetup,
		setPlanHolder,
	}
}

export const usePaymentFields = () => {
	// Payment Term Check Box Fields
	const paymentSinglePayCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.term === Term.SinglePay ||
				planHolder?.insurance.term === Term.TerminalIllness
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const payment3YearPayCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insurance.term === Term.ThreeYear ? 'Yes' : 'Off',
			format: false,
			section: 'payment',
		},
	]

	const payment5YearPayCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insurance.term === Term.FiveYear ? 'Yes' : 'Off',
			format: false,
			section: 'payment',
		},
	]

	const payment10YearPayCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insurance.term === Term.TenYear ? 'Yes' : 'Off',
			format: false,
			section: 'payment',
		},
	]

	const payment20YearPayCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: planHolder?.insurance.term === Term.TwentyYear ? 'Yes' : 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentModeSinglePayEFTFromCheckingCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType === PaymentType.SP_EFT
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentModeMultiPayEFTFromCheckingCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType === PaymentType.MP_EFT
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentModeOtherCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType !== PaymentType.SP_EFT &&
				planHolder?.insurance.paymentType !== PaymentType.MP_EFT
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentAnnuityCheck = (fieldName: string) => [
		{
			name: fieldName,
			value: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentTermChecks = (fields: Record<string, string>) => [
		...paymentSinglePayCheck(fields.singlePayCheck),
		...payment3YearPayCheck(fields.threeYearPayCheck),
		...payment5YearPayCheck(fields.fiveYearPayCheck),
		...payment10YearPayCheck(fields.tenYearPayCheck),
		...payment20YearPayCheck(fields.twentyYearPayCheck),
		...paymentAnnuityCheck(fields.annuityCheck),
	]

	// Payment Mode Date
	const paymentModePaymentDate = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType === PaymentType.MP_EFT ? '5' : '',
			format: false,
			section: 'payment',
		},
	]

	// Payment Mode Check Box Fields
	const paymentModeFields = (fields: Record<string, string>) => [
		...paymentModeSinglePayEFTFromCheckingCheck(
			fields.singlePayEFTFromCheckingCheck
		),
		...paymentModeMultiPayEFTFromCheckingCheck(
			fields.multiPayEFTFromCheckingCheck
		),
		...paymentModeOtherCheck(fields.otherCheck),
		...paymentModePaymentDate(fields.paymentDate),
	]

	// Payment Amount Fields
	const singlePayFaceAmount = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.singlePayFaceAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const singlePayPaymentToday = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.singlePayDueTodayAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const multiPayFaceAmount = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.multiPayFaceAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const multiPayMonthlyPayment = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.multiPayPeriodicAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const multiPayPaymentToday = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.multiPayDueTodayAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const totalFaceAmount = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.totalFaceAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const totalFirstPayment = (fieldName: string) => [
		{
			name: fieldName,
			value: paymentDetails?.totalDueTodayAmt || '',
			format: true,
			section: 'payment',
		},
	]

	const eftInitialPaymentBankName = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value: eftPaymentDetails?.initialAccount?.bankName ?? '',
				format: false,
				section: 'payment',
			},
		]
	}

	const totalPremiumAmount = (fieldName: string) => {
		return [
			{
				name: fieldName,
				value: paymentDetails?.totalPaidAmt || '',
				format: true,
				section: 'payment',
			},
		]
	}

	const paymentAmountFields = (fields: Record<string, string>) => [
		...singlePayFaceAmount(fields.singlePayFaceAmount),
		...singlePayPaymentToday(fields.singlePayPaymentToday),
		...multiPayFaceAmount(fields.multiPayFaceAmount),
		...multiPayMonthlyPayment(fields.multiPayMonthlyPayment),
		...multiPayPaymentToday(fields.multiPayPaymentToday),
		...totalFaceAmount(fields.totalFaceAmount),
		...totalFirstPayment(fields.totalFirstPayment),
		...totalPremiumAmount(fields.totalPremiumAmount),
	]

	const singlePayFields = (fields: Record<string, string>) => {
		return [
			...singlePayFaceAmount(fields.faceAmount),
			...singlePayPaymentToday(fields.paymentToday),
		]
	}

	const multiPayFields = (fields: Record<string, string>) => {
		return [
			...multiPayFaceAmount(fields.faceAmount),
			...multiPayMonthlyPayment(fields.monthlyPayment),
			...multiPayPaymentToday(fields.paymentToday),
		]
	}

	const initialBankRoutingNumber = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value: eftPaymentDetails?.initialAccount?.routingNumber ?? '',
				format: false,
				section: 'payment',
			},
		]
	}

	const initialBankAccountNumber = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value: eftPaymentDetails?.initialAccount?.accountNumber ?? '',
				format: false,
				section: 'payment',
			},
		]
	}

	const initialBankCheckingCheck = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value:
					eftPaymentDetails?.initialAccount?.accountType === 'checking'
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const initialBankSavingsCheck = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value:
					eftPaymentDetails?.initialAccount?.accountType === 'savings'
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const bankInfoSameCheck = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value:
					!eftPaymentDetails?.useDifferentOngoingAccount &&
					planHolder?.insurance.paymentType === PaymentType.MP_EFT
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankCheckingCheck = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value:
					eftPaymentDetails?.useDifferentOngoingAccount &&
					eftPaymentDetails?.ongoingAccount?.accountType === 'checking' &&
					planHolder?.insurance.paymentType === PaymentType.MP_EFT
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankSavingsCheck = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		return [
			{
				name: fieldName,
				value:
					eftPaymentDetails?.useDifferentOngoingAccount &&
					eftPaymentDetails?.ongoingAccount?.accountType === 'savings' &&
					planHolder?.insurance.paymentType === PaymentType.MP_EFT
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankMonthlyDrawDate = (fieldName: string) => {
		return [
			{
				name: fieldName,
				value:
					planHolder?.insurance.paymentType === PaymentType.MP_EFT ? '5' : '',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankName = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		const shouldFillThisField =
			eftPaymentDetails?.useDifferentOngoingAccount &&
			planHolder?.insurance.paymentType === PaymentType.MP_EFT

		return [
			{
				name: fieldName,
				value: shouldFillThisField
					? (eftPaymentDetails?.ongoingAccount?.bankName ?? '')
					: '',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankRoutingNumber = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		const shouldFillThisField =
			eftPaymentDetails?.useDifferentOngoingAccount &&
			planHolder?.insurance.paymentType === PaymentType.MP_EFT

		return [
			{
				name: fieldName,
				value: shouldFillThisField
					? (eftPaymentDetails?.ongoingAccount?.routingNumber ?? '')
					: '',
				format: false,
				section: 'payment',
			},
		]
	}

	const ongoingBankAccountNumber = (fieldName: string) => {
		const eftPaymentDetails = planHolder?.payment
			?.paymentDetails as EFTPayment['paymentDetails']

		const shouldFillThisField =
			eftPaymentDetails?.useDifferentOngoingAccount &&
			planHolder?.insurance.paymentType === PaymentType.MP_EFT

		return [
			{
				name: fieldName,
				value: shouldFillThisField
					? (eftPaymentDetails?.ongoingAccount?.accountNumber ?? '')
					: '',
				format: false,
				section: 'payment',
			},
		]
	}

	const creditCardNumber = (fieldName: string) => {
		const creditCardDetails = planHolder?.payment?.paymentDetails as CreditCard

		return [
			{
				name: fieldName,
				value:
					planHolder?.insurance.paymentType === PaymentType.CREDIT_CARD
						? (creditCardDetails?.token ?? '')
						: '',
				format: false,
				section: 'payment',
			},
		]
	}

	const creditCardMonthlyDrawDate = (fieldName: string) => {
		return [
			{
				name: fieldName,
				value:
					planHolder?.insurance.paymentType === PaymentType.CREDIT_CARD &&
					planHolder?.insurance.term !== Term.SinglePay
						? '5'
						: '',
				format: false,
				section: 'payment',
			},
		]
	}

	const singlePayEFTCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				paymentDetails?.singlePayDueTodayAmt !== 0 &&
				(planHolder?.insurance.paymentType === PaymentType.MP_EFT ||
					planHolder?.insurance.paymentType === PaymentType.SP_EFT)
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const singlePayCCCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.term === Term.SinglePay &&
				planHolder?.insurance.paymentType === PaymentType.CREDIT_CARD
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const multiPayEFTCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType === PaymentType.MP_EFT
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const multiPayCCCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.term !== Term.SinglePay &&
				planHolder?.insurance.paymentType === PaymentType.CREDIT_CARD
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const multiPayMonthlyEFTCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.paymentType === PaymentType.MP_EFT
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const multiPayMonthlyCCCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.term !== Term.SinglePay &&
				planHolder?.insurance.paymentType === PaymentType.CREDIT_CARD
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const multiPayMonthlyDirectBillCheck = (fieldName: string) => [
		{
			name: fieldName,
			value:
				planHolder?.insurance.term !== Term.SinglePay &&
				planHolder?.insurance.paymentType === PaymentType.BILL
					? 'Yes'
					: 'Off',
			format: false,
			section: 'payment',
		},
	]

	const paymentModeAndAmountFields = (fields: Record<string, string>) => {
		return [
			...singlePayPaymentToday(fields.singlePayPaymentToday),
			...singlePayEFTCheck(fields.singlePayEFTCheck),
			...multiPayPaymentToday(fields.multiPayPaymentToday),
			...multiPayEFTCheck(fields.multiPayEFTCheck),
			...multiPayCCCheck(fields.multiPayCCCheck),
			...multiPayMonthlyPayment(fields.multiPayMonthlyPayment),
			...multiPayMonthlyEFTCheck(fields.multiPayMonthlyEFTCheck),
			...multiPayMonthlyCCCheck(fields.multiPayMonthlyCCCheck),
			...multiPayMonthlyDirectBillCheck(fields.multiPayMonthlyDirectBillCheck),
		]
	}

	const eftInitialPaymentFields = (fields: Record<string, string>) => {
		return [
			...eftInitialPaymentBankName(fields.bankName),
			...initialBankRoutingNumber(fields.routingNumber),
			...initialBankAccountNumber(fields.accountNumber),
			...initialBankCheckingCheck(fields.checkingCheck),
			...initialBankSavingsCheck(fields.savingsCheck),
		]
	}

	const eftOngoingPaymentFields = (fields: Record<string, string>) => {
		return [
			...bankInfoSameCheck(fields.bankInfoSameCheck),
			...ongoingBankMonthlyDrawDate(fields.bankMonthlyDrawDate),
			...ongoingBankName(fields.bankName),
			...ongoingBankRoutingNumber(fields.routingNumber),
			...ongoingBankAccountNumber(fields.accountNumber),
			...ongoingBankCheckingCheck(fields.checkingCheck),
			...ongoingBankSavingsCheck(fields.savingsCheck),
		]
	}

	const creditCardPaymentFields = (fields: Record<string, string>) => {
		return [
			...creditCardNumber(fields.creditCardNumber),
			...creditCardMonthlyDrawDate(fields.creditCardMonthlyDrawDate),
		]
	}

	const monthlyBillCheck = (fieldName: string) => {
		const paymentDetails = planHolder?.payment?.paymentDetails as DirectBill

		return [
			{
				name: fieldName,
				value:
					paymentDetails?.billingType === 'Monthly' &&
					planHolder?.insurance.paymentType === PaymentType.BILL
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const quarterlyBillCheck = (fieldName: string) => {
		const paymentDetails = planHolder?.payment?.paymentDetails as DirectBill

		return [
			{
				name: fieldName,
				value:
					paymentDetails?.billingType === 'Quarterly' &&
					planHolder?.insurance.paymentType === PaymentType.BILL
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const semiAnnualBillCheck = (fieldName: string) => {
		const paymentDetails = planHolder?.payment?.paymentDetails as DirectBill

		return [
			{
				name: fieldName,
				value:
					paymentDetails?.billingType === 'Semi-Annual' &&
					planHolder?.insurance.paymentType === PaymentType.BILL
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const annualBillCheck = (fieldName: string) => {
		const paymentDetails = planHolder?.payment?.paymentDetails as DirectBill

		return [
			{
				name: fieldName,
				value:
					paymentDetails?.billingType === 'Annual' &&
					planHolder?.insurance.paymentType === PaymentType.BILL
						? 'Yes'
						: 'Off',
				format: false,
				section: 'payment',
			},
		]
	}

	const directBillCheckboxFields = (fields: Record<string, string>) => {
		return [
			...monthlyBillCheck(fields.monthlyBillCheck),
			...quarterlyBillCheck(fields.quarterlyBillCheck),
			...semiAnnualBillCheck(fields.semiAnnualBillCheck),
			...annualBillCheck(fields.annualBillCheck),
		]
	}

	return {
		// Payment Term Checkboxes
		paymentSinglePayCheck,
		payment3YearPayCheck,
		payment5YearPayCheck,
		payment10YearPayCheck,
		payment20YearPayCheck,
		paymentAnnuityCheck,
		paymentTermChecks,

		// Payment Mode Checkboxes
		paymentModeSinglePayEFTFromCheckingCheck,
		paymentModeMultiPayEFTFromCheckingCheck,
		paymentModeOtherCheck,
		paymentModePaymentDate,
		paymentModeFields,

		// Payment Amount Fields
		singlePayFaceAmount,
		singlePayPaymentToday,
		singlePayFields,
		multiPayFaceAmount,
		multiPayMonthlyPayment,
		multiPayPaymentToday,
		multiPayFields,
		totalFaceAmount,
		totalFirstPayment,
		totalPremiumAmount,
		singlePayEFTCheck,
		singlePayCCCheck,
		multiPayEFTCheck,
		multiPayCCCheck,
		multiPayMonthlyEFTCheck,
		multiPayMonthlyCCCheck,
		multiPayMonthlyDirectBillCheck,
		paymentModeAndAmountFields,
		eftInitialPaymentBankName,
		initialBankRoutingNumber,
		initialBankAccountNumber,
		initialBankCheckingCheck,
		initialBankSavingsCheck,
		eftInitialPaymentFields,
		ongoingBankMonthlyDrawDate,
		ongoingBankName,
		ongoingBankRoutingNumber,
		ongoingBankAccountNumber,
		bankInfoSameCheck,
		ongoingBankCheckingCheck,
		ongoingBankSavingsCheck,
		eftOngoingPaymentFields,
		creditCardNumber,
		creditCardMonthlyDrawDate,
		creditCardPaymentFields,
		monthlyBillCheck,
		quarterlyBillCheck,
		semiAnnualBillCheck,
		annualBillCheck,
		directBillCheckboxFields,
		paymentAmountFields,
	}
}
